import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './ErrorBoundary.scss';

/**
 * ErrorBoundary Component
 *
 * A reusable error boundary that catches JavaScript errors in child components,
 * logs them, and displays a fallback UI instead of crashing the whole app.
 *
 * @component
 * @example
 * ```jsx
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <ChildComponent />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    this.setState({ errorInfo });

    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Render custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Render default fallback UI
      return (
        <div className='error-boundary'>
          <div className='error-boundary-content'>
            <h3 className='error-boundary-title'>
              {this.props.title || 'Something went wrong'}
            </h3>
            <p className='error-boundary-message'>
              {this.props.message ||
                'An error occurred while loading this component.'}
            </p>
            {this.props.showRetry && (
              <button
                className='error-boundary-retry'
                onClick={this.handleRetry}
              >
                Try Again
              </button>
            )}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className='error-boundary-details'>
                <summary>Error Details</summary>
                <pre>{this.state.error.toString()}</pre>
                {this.state.errorInfo && (
                  <pre>{this.state.errorInfo.componentStack}</pre>
                )}
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  /** Child components to render */
  children: PropTypes.node.isRequired,
  /** Custom fallback UI to render on error */
  fallback: PropTypes.node,
  /** Title for default fallback UI */
  title: PropTypes.string,
  /** Message for default fallback UI */
  message: PropTypes.string,
  /** Whether to show retry button */
  showRetry: PropTypes.bool,
  /** Callback when error is caught */
  onError: PropTypes.func,
};

ErrorBoundary.defaultProps = {
  showRetry: true,
};

export { ErrorBoundary };
